import {
    Box,
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    IconButton
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavigatedComponent from "../NavigatedComponent";
import Header from "../../common/Header";
import Sidenav from "../../common/Sidenav";
import IconSidenav from "../../common/IconSidenav";
import { Edit, Delete, Add } from "@mui/icons-material";
import { apiList, invokeApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";

const ArticleList = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [cookies] = useCookies();
    const navigate = useNavigate();
    const location = useLocation();
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [showSideNav, setShowSideNav] = useState(false);

    const isMobileScreen = useMediaQuery("(min-width:360px) and (max-width:500px)");

    const fetchArticles = async () => {
        setLoading(true);
        try {
            const response = await invokeApi(config.getMyCollege + apiList.getArticles, {}, cookies);
            if (response?.status === 200 && response?.data?.articles) {
                setArticles(response.data.articles);
            } else {
                setArticles([]);
            }
        } catch (error) {
            console.error("Error fetching articles:", error);
            setSnackbar({ open: true, message: "Failed to fetch articles", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav);
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            const response = await invokeApi(
                config.getMyCollege + apiList.deleteArticle,
                { id: deleteId },
                cookies
            );
            if (response?.status >= 200 && response?.status < 300) {
                setSnackbar({ open: true, message: "Article deleted successfully", severity: "success" });
                fetchArticles();
            } else {
                setSnackbar({ open: true, message: "Failed to delete article", severity: "error" });
            }
        } catch (error) {
            console.error("Error deleting article:", error);
            setSnackbar({ open: true, message: "Error deleting article", severity: "error" });
        } finally {
            setDeleteId(null);
        }
    };

    return (
        <Grid sx={{ display: "flex" }}>
            {isMobileScreen ? (
                <IconSidenav />
            ) : showSideNav ? (
                <IconSidenav />
            ) : (
                <Sidenav />
            )}
            <Grid component="main" sx={{ width: "100%", px: 1 }}>
                <Header toggleSideNav={toggleSideNav} />
                <Grid sx={{ py: 1 }}>
                    <NavigatedComponent pathname={location.pathname} />
                    <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5, py: 3 }}>
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h5" fontWeight="bold">Articles</Typography>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate("/publication-manager/add-article")}
                                sx={{ bgcolor: "#262272" }}
                            >
                                Add Article
                            </Button>
                        </Grid>

                        <Grid item xs={12}>
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                                            <TableRow>
                                                <TableCell><strong>Year</strong></TableCell>
                                                <TableCell><strong>Category</strong></TableCell>
                                                <TableCell><strong>Citation/Content</strong></TableCell>
                                                <TableCell align="right"><strong>Actions</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {articles.length > 0 ? (
                                                articles.map((article) => (
                                                    <TableRow key={article.id}>
                                                        <TableCell>{article.year}</TableCell>
                                                        <TableCell>{article.categoryName || article.category || article.category_name || article.title || "-"}</TableCell>
                                                        <TableCell>
                                                            <div dangerouslySetInnerHTML={{ __html: article.content.substring(0, 100) + "..." }} />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton color="primary" onClick={() => navigate(`/publication-manager/edit-article/${article.id}`)}>
                                                                <Edit />
                                                            </IconButton>
                                                            <IconButton color="error" onClick={() => setDeleteId(article.id)}>
                                                                <Delete />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center">No articles found</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>

                        {/* Delete Confirmation Dialog */}
                        <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                            <DialogTitle>Confirm Delete</DialogTitle>
                            <DialogContent>Are you sure you want to delete this article?</DialogContent>
                            <DialogActions>
                                <Button onClick={() => setDeleteId(null)}>Cancel</Button>
                                <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                            </DialogActions>
                        </Dialog>

                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                        >
                            <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ArticleList;
